import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import NavBar from "../NavBar/navBar";
import { getCategories, getMujjalahList, getNoorDiaryList, createEntry } from "./.request";
import { useAuth } from "../context/AuthContext";
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import OutlinedInput from '@mui/material/OutlinedInput';
import Checkbox from '@mui/material/Checkbox';
import ListItemText from '@mui/material/ListItemText';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';

const CreateMunasibaat = () => {
  const navigate = useNavigate();
  const { authToken } = useAuth();

  const [categories, setCategories] = useState([]);
  const [mujjalahList, setMujjalahList] = useState([]);
  const [noorDiaryList, setNoorDiaryList] = useState([]);

  const [categoryId, setCategoryId] = useState('');
  const [year, setYear] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [images, setImages] = useState([]);
  const [videoUrls, setVideoUrls] = useState(['']);
  const [selectedMujjalahIds, setSelectedMujjalahIds] = useState([]);
  const [selectedNoorDiaryIds, setSelectedNoorDiaryIds] = useState([]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    async function loadData() {
      const catRes = await getCategories(authToken);
      if (catRes.data || catRes.Categories) {
        setCategories(catRes.data || catRes.Categories || []);
      }
      const mRes = await getMujjalahList(authToken);
      if (mRes.data) {
        setMujjalahList(mRes.data);
      }
      const dRes = await getNoorDiaryList(authToken);
      if (dRes.data) {
        setNoorDiaryList(dRes.data);
      }
    }
    loadData();
  }, [authToken]);

  // Handle multiple images selection (3 to 5 images recommended)
  const handleImagesChange = (e) => {
    const files = Array.from(e.target.files);
    if (images.length + files.length > 5) {
      alert("You can upload a maximum of 5 images.");
      return;
    }

    files.forEach((file) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImages((prev) => [...prev, reader.result]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removeImage = (index) => {
    setImages((prev) => prev.filter((_, i) => i !== index));
  };

  // Video links management
  const handleVideoChange = (index, value) => {
    const updated = [...videoUrls];
    updated[index] = value;
    setVideoUrls(updated);
  };

  const addVideoField = () => {
    setVideoUrls((prev) => [...prev, '']);
  };

  const removeVideoField = (index) => {
    setVideoUrls((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!categoryId) {
      setError("Please select a Munasibat Category.");
      return;
    }
    if (!year || !year.trim()) {
      setError("Year (e.g. 1446 AH or 2024) is required.");
      return;
    }
    if (images.length < 1) {
      setError("Please select at least 1 cover/event image (3 to 5 recommended).");
      return;
    }

    setLoading(true);
    setError('');

    const payload = {
      category_id: categoryId,
      year: year.trim(),
      title: title.trim(),
      description,
      images,
      videos: videoUrls.filter((v) => v.trim() !== ''),
      mujjalah_ids: selectedMujjalahIds,
      noor_diary_ids: selectedNoorDiaryIds,
    };

    const res = await createEntry(authToken, payload);
    setLoading(false);

    if (res.success || res.entryId) {
      alert("Munasibat yearly entry created successfully!");
      navigate("/munasibaat");
    } else {
      setError(res.errors ? res.errors.join(", ") : "Failed to create entry.");
    }
  };

  return (
    <>
      <NavBar />
      <div className="max-w-4xl mx-auto px-6 py-8">
        <div className="bg-white rounded-lg shadow-lg border p-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-6 border-b pb-3">Create Munasibat Year Entry</h2>
          
          {error && <div className="mb-6 p-4 bg-red-100 border-l-4 border-red-500 text-red-700 rounded">{error}</div>}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Category Selector */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Select Munasibat Category <span className="text-red-500">*</span>
              </label>
              <select
                className="w-full border rounded-lg px-4 py-2.5 bg-gray-50 focus:bg-white focus:ring-2 focus:ring-blue-500"
                value={categoryId}
                onChange={(e) => setCategoryId(e.target.value)}
                required
              >
                <option value="">-- Choose Category (e.g. Muharram, Ayyam e Fatimiya) --</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>{cat.title}</option>
                ))}
              </select>
              {categories.length === 0 && (
                <p className="text-xs text-amber-600 mt-1">
                  No category exists yet. Please create a category first in the Munasibaat dashboard.
                </p>
              )}
            </div>

            {/* Year & Title */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Year / Edition <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  placeholder="e.g. 1446 AH or 2024 AD"
                  className="w-full border rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-blue-500"
                  value={year}
                  onChange={(e) => setYear(e.target.value)}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Event Title (Optional)
                </label>
                <input
                  type="text"
                  placeholder="e.g. Salana Majalis Muharram ul Haram"
                  className="w-full border rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-blue-500"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                />
              </div>
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Description</label>
              <textarea
                rows={4}
                placeholder="Detailed description of the event, speeches, highlights..."
                className="w-full border rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-blue-500"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>

            {/* Images Uploader (3 to 5 Images) */}
            <div className="bg-gray-50 p-4 rounded-lg border">
              <label className="block text-sm font-bold text-gray-800 mb-1">
                Upload Event Images (3 - 5 Images) <span className="text-red-500">*</span>
              </label>
              <p className="text-xs text-gray-500 mb-3">Upload high quality cover and gallery images for this yearly event.</p>
              
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handleImagesChange}
                disabled={images.length >= 5}
                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-blue-600 file:text-white hover:file:bg-blue-700"
              />

              {images.length > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 mt-4">
                  {images.map((img, idx) => (
                    <div key={idx} className="relative group border rounded overflow-hidden">
                      <img src={img} alt={`Uploaded ${idx + 1}`} className="w-full h-24 object-cover" />
                      <button
                        type="button"
                        onClick={() => removeImage(idx)}
                        className="absolute top-1 right-1 bg-red-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs opacity-90 hover:opacity-100"
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Video Links */}
            <div className="bg-gray-50 p-4 rounded-lg border">
              <label className="block text-sm font-bold text-gray-800 mb-2">Video Links / Embed URLs</label>
              {videoUrls.map((v, idx) => (
                <div key={idx} className="flex gap-2 mb-2">
                  <input
                    type="text"
                    placeholder="e.g. https://www.youtube.com/watch?v=... or direct MP4 link"
                    className="flex-1 border rounded-lg px-4 py-2 bg-white"
                    value={v}
                    onChange={(e) => handleVideoChange(idx, e.target.value)}
                  />
                  {videoUrls.length > 1 && (
                    <Button variant="outlined" color="error" size="small" onClick={() => removeVideoField(idx)}>
                      Remove
                    </Button>
                  )}
                </div>
              ))}
              <Button size="small" variant="text" onClick={addVideoField}>
                + Add Another Video URL
              </Button>
            </div>

            {/* Related Mujjalah Multi-Select */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-bold text-gray-800 mb-2">Link Related Mujallah Magazines</label>
                <FormControl fullWidth size="small">
                  <InputLabel>Select Mujallah Magazines</InputLabel>
                  <Select
                    multiple
                    value={selectedMujjalahIds}
                    onChange={(e) => setSelectedMujjalahIds(e.target.value)}
                    input={<OutlinedInput label="Select Mujallah Magazines" />}
                    renderValue={(selected) => `${selected.length} Selected`}
                  >
                    {mujjalahList.map((m) => (
                      <MenuItem key={m.id} value={m.id}>
                        <Checkbox checked={selectedMujjalahIds.indexOf(m.id) > -1} />
                        <ListItemText primary={`Mujallah Year: ${m.year} (${m.saal || 'N/A'})`} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>

              {/* Related Noor Diary Multi-Select */}
              <div>
                <label className="block text-sm font-bold text-gray-800 mb-2">Link Related Noor Diary</label>
                <FormControl fullWidth size="small">
                  <InputLabel>Select Noor Diary Entries</InputLabel>
                  <Select
                    multiple
                    value={selectedNoorDiaryIds}
                    onChange={(e) => setSelectedNoorDiaryIds(e.target.value)}
                    input={<OutlinedInput label="Select Noor Diary Entries" />}
                    renderValue={(selected) => `${selected.length} Selected`}
                  >
                    {noorDiaryList.map((d) => (
                      <MenuItem key={d.id} value={d.id}>
                        <Checkbox checked={selectedNoorDiaryIds.indexOf(d.id) > -1} />
                        <ListItemText primary={`Noor Diary Year: ${d.year} (${d.saal || 'N/A'})`} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>
            </div>

            {/* Actions */}
            <div className="pt-4 flex justify-end gap-3 border-t">
              <Button variant="outlined" color="secondary" onClick={() => navigate('/munasibaat')}>
                Cancel
              </Button>
              <Button type="submit" variant="contained" color="primary" disabled={loading}>
                {loading ? <CircularProgress size={24} color="inherit" /> : 'Save Entry'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default CreateMunasibaat;